// DEVIATION from the plan: relative imports instead of the '@' alias, for the
// same reason as useGeneratedCourse.ts — jest has no moduleNameMapper for '@'
// and jest.config.js may not be touched. See useGeneratedCourse.ts for detail.
import ChessRestClient from '../../Api/ChessRestClient';
import {getLocalStorage} from '../../Utils/CommonUtils';
import {
  QueryClient,
  useMutation,
  UseMutationResult,
  useQueryClient,
} from '@tanstack/react-query';

import {withPageCompleted} from '../../Screens/LMS/courseProgress';
import {GeneratedCourse, ProgressPayload} from './types';

export async function postCourseProgress(payload: ProgressPayload) {
  const token = await getLocalStorage('access_token');
  const response = await ChessRestClient.post('/lms/v1/user-progress', payload, {
    headers: {Authorization: `Bearer ${token}`},
  });
  return response.data;
}

/**
 * Bring the caches up to date with a page the server has just recorded.
 *
 * The course is cached for half an hour, so without this a student who left
 * and came back inside that window would resume from where they were when it
 * was fetched. Marked only when the server marks it too: a video counts once
 * it was watched to the end. The list is refetched for its progress bar.
 */
export function afterProgressSaved(qc: QueryClient, payload: ProgressPayload) {
  const finished = payload.content_type !== 'video' || !!payload.video_watched;
  if (finished) {
    qc.setQueriesData<GeneratedCourse | undefined>(['generated-course'], course =>
      course?.book?.id === payload.book_id
        ? withPageCompleted(course, payload.page_id)
        : course,
    );
  }
  qc.invalidateQueries(['generated-course-list']);
}

export function useCourseProgress(): UseMutationResult<
  unknown,
  Error,
  ProgressPayload
> {
  const qc = useQueryClient();
  return useMutation<unknown, Error, ProgressPayload>(postCourseProgress, {
    onSuccess: (_data, payload) => afterProgressSaved(qc, payload),
  });
}
